/** Every hub source (split from master-layout.tsx's search pipeline), mapped and built into the view. */
import { useEffect, useMemo, useSyncExternalStore } from "react";
import { conversationBrowserAccountKey } from "@/hooks/conversation-browser-cache";
import { subscribeDesktopChatAuthScope } from "@/desktop/desktop-chat-auth-scope";
import type { WorkspaceSearchKind } from "@/api/workspace-search";
import {
  SETTINGS_PAGES,
  currentSettingsPlatform,
} from "@/components/settings/settings-navigation";
import type { HubChip } from "@/lib/global-search";
import { useRecentOpened } from "@/lib/recent-opened";
import { useAppLanguage } from "@/i18n";
import { useHubMemory } from "./use-hub-memory";
import { useNotificationStore } from "@/stores/notification-store";
import { useProjectStore } from "@/stores/project-store";
import { normalizeHubQuery } from "./hub-ranking";
import {
  agentResult,
  chatResult,
  frameResult,
  memoryResult,
  projectResult,
  recentResult,
  settingResult,
  taskResult,
} from "./hub-results";
import { buildHubView, type HubSources } from "./hub-sections";
import type { HubGroupId, HubView } from "./hub-types";
import { useHubChats, type HubChats } from "./use-hub-chats";
import { useHubSearch } from "./use-hub-search";

function searchKinds(chip: HubChip, typing: boolean): WorkspaceSearchKind[] {
  if (chip === "all") return typing ? ["tasks", "frames", "agents"] : [];
  if (chip === "tasks") return ["tasks"];
  if (chip === "frames") return ["frames"];
  return [];
}

export interface HubDataOptions {
  chip: HubChip;
  query: string;
  projectId: string | null;
  archivedExpanded: boolean;
  expandedGroups: ReadonlySet<HubGroupId>;
}

export function useHubData({
  chip,
  query,
  projectId,
  archivedExpanded,
  expandedGroups,
}: HubDataOptions): {
  view: HubView;
  chats: HubChats;
  notificationCount: number;
} {
  const scope = useSyncExternalStore(
    subscribeDesktopChatAuthScope,
    conversationBrowserAccountKey,
    () => null,
  );
  const typing = normalizeHubQuery(query).length > 0;
  const chats = useHubChats({ query, projectId });
  const search = useHubSearch({
    query,
    projectId,
    kinds: searchKinds(chip, typing),
  });
  const summary = useNotificationStore((s) => s.summary);
  const notificationLoading = useNotificationStore((s) => s.summaryLoading);
  const notificationError = useNotificationStore((s) => s.summaryError);
  const fetchSummary = useNotificationStore((s) => s.fetchSummary);
  const hubMemory = useHubMemory(
    query,
    typing && (chip === "all" || chip === "memory"),
  );
  const projects = useProjectStore((s) => s.projects);
  const recent = useRecentOpened();
  // Section titles, second lines and settings page titles are worded in the
  // app language when the view is built, so a switch rebuilds it.
  const language = useAppLanguage();

  useEffect(() => {
    void fetchSummary();
  }, [fetchSummary, scope]);

  const view = useMemo(() => {
    const platform = currentSettingsPlatform();
    const archived = chats.archived.map(chatResult);
    const sources: HubSources = {
      notificationLoading,
      notificationError,
      searchError: search.error,
      memoryLoading: hubMemory.loading,
      memoryError: hubMemory.error,
      approvals: summary.approvals,
      rows: summary.rows,
      chats: {
        active: chats.active.map(chatResult),
        archived,
        archivedCount: `${archived.length}${chats.archivedHasMore ? "+" : ""}`,
        loading: chats.loading,
        error: chats.error,
        hasMore: chats.hasMore,
        archivedHasMore: chats.archivedHasMore,
      },
      tasks: {
        results: (search.results.tasks?.items ?? []).map(taskResult),
        total: search.results.tasks?.total ?? 0,
        loading: search.loading,
      },
      frames: {
        results: (search.results.frames?.items ?? []).map(frameResult),
        total: search.results.frames?.total ?? 0,
        loading: search.loading,
      },
      agents: (search.results.agents?.items ?? []).map(agentResult),
      memory: hubMemory.things.flatMap((thing) =>
        thing.id ? [memoryResult({ ...thing, id: thing.id })] : [],
      ),
      projects: projects
        .filter((project) => project.status === "active")
        .map(projectResult),
      settings: Object.values(SETTINGS_PAGES)
        .filter(
          (page) =>
            page.searchable &&
            (!page.platforms || page.platforms.includes(platform)),
        )
        .map(settingResult),
      recent: recent.map(recentResult),
    };
    return buildHubView(sources, {
      chip,
      query,
      projectId,
      projectName:
        projects.find((project) => project.id === projectId)?.name ?? null,
      archivedExpanded,
      expandedGroups,
      now: Date.now(),
    });
    // `language` is read through `translate` while the view is built.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    archivedExpanded,
    chats,
    chip,
    expandedGroups,
    hubMemory.things,
    hubMemory.loading,
    hubMemory.error,
    projectId,
    projects,
    query,
    recent,
    search,
    summary,
    notificationLoading,
    notificationError,
    language,
  ]);

  return { view, chats, notificationCount: summary.count };
}
